import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import { Box, Button, CircularProgress, Stack, TextField, Tooltip } from '@mui/material';
import { debounce } from 'lodash';
import * as React from 'react';
import MaterialArtifactEditor from '../../app/material-artifact-editor';
import { CustomSearch } from '../../general/customSearch';
import { colors } from '../../../../lib/teacup/lib/colors';

import { Theme } from '@mui/material/styles';
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { ARTIFACT_TYPES, TEMPLATE_CONTENT_CHANGE_INDICATOR_SHOW_TIME, TEMPLATE_EDITOR_MODAL_TEXTS } from '../../../constants';
import { ITeam, IUser } from '../../../stores/jotai/base';
import { TemplateStateInterface, setSelectedTemplate } from '../../../stores/jotai/template/templates';
import { TUpdateTemplatePayload } from "../../../tanstackQuery/queries/templates/type";
import { WithTemplates } from "../../hoc/WithTemplates";
import TemplateCreationForm from './TemplateCreationForm';
import TemplateList from './TemplateList';
import { STOCK_THEMES } from "../../../util/stockThemes";
interface Props {
    team: ITeam;
    user: IUser;
    org: OrgState;
    templateAtomState: TemplateStateInterface,
    setTemplateAtomState: Function,
    savingTemplate: boolean;
    refreshTemplateList: () => void;
    closeTemplateView: () => void;
    loadTemplateData: (templateID: string) => void;
    discoverySession: any;
    setBase: Function;
    setSavingTemplateChanges: Function;
    setShouldBlockModalClose: Function;
    shouldBlockModalClose: boolean;
    hideBackButton?: boolean;
    reflectRealtimeTemplateUpdate: boolean;
    setReflectRealtimeTemplateUpdate: Function;
    isTeamMember: boolean;
    updateTemplate: UseMutateAsyncFunction<any, unknown, TUpdateTemplatePayload, unknown>;
    templates?:any[];
    filteredTemplatesUsers?:object;
    theme: Theme;
    setTemplateTitle:Function;
}

interface State {
    newTemplatePrompt: boolean;
    newTemplateName: string;
    renaming: boolean;
    hasSavedYet: boolean;
    savingNew: boolean;
    valueCache1: string;
    // searchResults: [];
    searchValue: string;
    // entries: [];
    editedTemplateName: string;
    templateCreationError: any;
    showTemplateCreationForm: boolean;
    templateIDBeingDeleted: string;
    savingTemplateContent: boolean;
    savingTemplateName: boolean;
}

interface IRefs {
    [string: string]: any; // required for type compat

    editor: {
        getText: Function;
        getEditor: Function;
        focus: Function;
        update: Function;
        getContents: Function;
        getEditorContents: Function;
    };
}

class TemplateEditorClass extends React.Component<Props, State> {

    refs: IRefs;

    // Start in the new template prompt
    state: State = {
        newTemplatePrompt: false,
        newTemplateName: "",
        editedTemplateName: "",
        renaming: false,
        hasSavedYet: false,
        savingNew: false,
        valueCache1: '',
        // searchResults: undefined,
        searchValue: '',
        // entries: [],
        templateCreationError: null,
        showTemplateCreationForm: false,
        templateIDBeingDeleted: null,
        savingTemplateContent: false,
        savingTemplateName: false,
    };

    shouldUpdateTemplateNameFromProps = true;
    templateContentSavingTimeout = null;

    canSaveEditedTemplateName = () => {
        const { shouldBlockModalClose } = this.props;

        return !shouldBlockModalClose;
    };

    saveEditedTemplateName = async () => {
        const { selectedTemplateName } = this.props.templateAtomState;
        const { editedTemplateName } = this.state;
        const {
            templateAtomState,
            setTemplateAtomState,
            discoverySession,
            team,
            user,
            setBase,
            setSavingTemplateChanges,
        } = this.props;
        const { selectedTemplateID } = templateAtomState;

        if (
            this.canSaveEditedTemplateName() &&
            editedTemplateName !== selectedTemplateName
        ) {
            this.setState({
                savingTemplateName: true,
            })

           await this.props.updateTemplate({
                groupID: team.groupID,
                templateID: selectedTemplateID,
                name: editedTemplateName,
                userID: user.userID,
                sessionID: discoverySession?.sessionID
            })
        }
    };

    debouncedSaveTemplateNameFn = debounce(this.saveEditedTemplateName, 450);

    componentDidMount(): void {
        const { selectedTemplateName } = this.props.templateAtomState;

        this.setState({
            editedTemplateName: selectedTemplateName,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any
    ): void {
        const {
            setShouldBlockModalClose,
            templateAtomState,
            setSavingTemplateChanges,
            reflectRealtimeTemplateUpdate,
            setReflectRealtimeTemplateUpdate,
        } = this.props;
        const {
            editedTemplateName,
            savingTemplateContent,
            savingTemplateName,
        } = this.state;
        const { selectedTemplateName, selectedTemplateID } = templateAtomState;

        if(!!selectedTemplateName && reflectRealtimeTemplateUpdate) {
            this.setState({
                editedTemplateName: selectedTemplateName,
            });

            setReflectRealtimeTemplateUpdate(false);
        }
        else if (
            !!selectedTemplateName &&
            prevProps.templateAtomState.selectedTemplateName !==
                selectedTemplateName
        ) {
            if (this.shouldUpdateTemplateNameFromProps) {
                this.setState({
                    editedTemplateName: selectedTemplateName,
                });
                this.shouldUpdateTemplateNameFromProps = false;
            } 
        }

        if (prevState.editedTemplateName !== editedTemplateName) {
            this.debouncedSaveTemplateNameFn();
            setShouldBlockModalClose(!!selectedTemplateName && !editedTemplateName);
        }

        if (!selectedTemplateID) {
            setSavingTemplateChanges(false);
        } else if (
            prevState.savingTemplateName !== savingTemplateName ||
            prevState.savingTemplateContent !== savingTemplateContent
        ) {
            setSavingTemplateChanges(savingTemplateName || savingTemplateContent);
        } 
    }

    handleTemplateContentChange = () => {
        this.setState({
            savingTemplateContent: true,
        });

        clearTimeout(this.templateContentSavingTimeout);

        this.templateContentSavingTimeout = setTimeout(() => {
            this.setState({
                savingTemplateContent: false,
            });
        }, TEMPLATE_CONTENT_CHANGE_INDICATOR_SHOW_TIME);
    }

    renderTemplateEditor = () => {
        const { setTemplateAtomState, hideBackButton, theme,setTemplateTitle } = this.props;
        const { selectedTemplateID, selectedTemplateName } =
            this.props.templateAtomState;

        return (
            <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                sx={{ maxHeight: "70vh", top: "0px" }}
            >
                <Stack
                    sx={{ marginBottom: "20px" }}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >
                    {!hideBackButton && (
                        <Tooltip
                            title={
                                this.canSaveEditedTemplateName()
                                    ? TEMPLATE_EDITOR_MODAL_TEXTS.MODAL_HEADER.BACK_BUTTON
                                            .NORMAL.TOOLTIP
                                    : TEMPLATE_EDITOR_MODAL_TEXTS.MODAL_HEADER.CLOSE_BUTTON
                                            .EMPTY_TEMPLATE_NAME.TOOLTIP
                            }
                        >
                            <Box>
                                <Button
                                    variant="outlined"
                                    sx={{ minWidth: "unset", padding: "6px" }}
                                    disabled={!this.canSaveEditedTemplateName()}
                                    onClick={() => {
                                        setSelectedTemplate(setTemplateAtomState, null);
                                        setTemplateTitle("Template List")
                                        this.shouldUpdateTemplateNameFromProps = true;
                                    }}
                                >
                                    <ArrowBackIosOutlinedIcon sx={{ fontSize: "20px" }} />
                                </Button>
                            </Box>
                        </Tooltip>
                    )}
                    <Tooltip
                        title="Template title cannot be empty"
                        open={!this.canSaveEditedTemplateName()}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    background: STOCK_THEMES.v3.palette.error.main,
                                    color: theme.custom.white,
                                    pointerEvents: "none",
                                    ".MuiTooltip-arrow": {
                                        color: STOCK_THEMES.v3.palette.error.main,
                                    },
                                },
                            },
                        }}
                    >
                        <TextField
                            sx={{
                                ".MuiInputBase-root": {
                                    fontSize: "18px",
                                    borderColor: theme.custom.mainButtonHighlight,
                                },
                                ".MuiInputBase-input": {
                                    padding: "5px 8px",
                                },
                                ".MuiOutlinedInput-notchedOutline": {
                                    borderColor: "transparent",
                                },
                                ".MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: !this.canSaveEditedTemplateName()
                                            ? STOCK_THEMES.v3.palette.error.main
                                            : theme?.custom.mainButtonHighlight,
                                    },
                            }}
                            size="small"
                            value={this.state.editedTemplateName}
                            onChange={(event) => {
                                this.setState({
                                    editedTemplateName: event.target.value,
                                });
                            }}
                            error={!this.canSaveEditedTemplateName()}
                        />
                    </Tooltip>
                </Stack>
                <Box
                    ref={(ref) => {
                        this.TemplateEditor = ref;
                    }}
                    id="template-editor-toolbar-portal-target"
                ></Box>
                <Box
                    sx={{
                        width: "100%",
                        marginTop: "10px",
                        height: "10px",
                        display: "block",
                        borderBottom: "1px solid " + colors.PRIMARY_NEUTRAL_GREY,
                    }}
                ></Box>
                <Box
                    sx={{
                        ".remirror-editor": {
                            height: "400px",
                        },
                    }}
                >
                    <MaterialArtifactEditor
                        portal={() => {
                            return this.TemplateEditor;
                        }}
                        templateID={selectedTemplateID}
                        artifactID={selectedTemplateID}
                        artifactType={ARTIFACT_TYPES.TEMPLATE}
                        onChange={this.handleTemplateContentChange}
                        useToolBar={true}
                    />
                </Box>
            </Stack>
        );
    };

    renderTemplateList = () => {
        return (
            <Stack
                sx={{
                    marginTop: "24px !important",
                    maxHeight: "70vh",
                    ".custom-search-container": {
                        flexShrink: 0,
                    }
                }}
            >
                <CustomSearch
                    focusOnMount={true}
                    inputValue={this.state.searchValue}
                    updateInputValue={(newVal) =>
                        this.setState({ searchValue: newVal.toLowerCase() })
                    }
                    placeholder="Search Templates..."
                />
                <Box
                    sx={{
                        // flexGrow: 1,
                        overflow:"hidden",
                        marginTop: "8px",
                    }}
                >
                    <TemplateList searchValue={this.state.searchValue} filteredTemplates={this.props.templates} filteredTemplatesUsers={this.props.filteredTemplatesUsers}/>
                </Box>
            </Stack>
        );
    }
    TemplateEditor: HTMLDivElement;

    handleTemplateCreationFormClose = () => {
        this.setState({
            showTemplateCreationForm: false,
        });
    }

    handleTemplateCreationFormOpen = () => {
        this.setState({
            showTemplateCreationForm: true,
        });
    }

    handleSuccessfulTemplateCreation = (response) => {
        const { setTemplateAtomState } = this.props;

        this.handleTemplateCreationFormClose();
        setSelectedTemplate(setTemplateAtomState, response);
    }

    render() {
        const { hideBackButton,theme } = this.props;
        const { showTemplateCreationForm } = this.state;
        const { selectedTemplateID } = this.props.templateAtomState;

        if (!!selectedTemplateID || (!selectedTemplateID && hideBackButton)) {
            const {setTemplateTitle} = this.props
            setTemplateTitle("Template")
            return this.renderTemplateEditor();
        }

        return (
            <Stack
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={0.5}
            >
                <Box>
                    {showTemplateCreationForm ? (
                        <TemplateCreationForm
                            inline={true}
                            onClose={this.handleTemplateCreationFormClose}
                            onSuccess={this.handleSuccessfulTemplateCreation}
                            team = {this.props.team}
                            user = {this.props.user}
                            setBase = {this.props.setBase}
                        />
                    ) : (
                        <Button
                            variant="outlined"
                            sx={{ 
                                flexGrow: 1,
                                color:theme?.custom.mainIconColor,
                                borderColor:theme?.custom.borderGrey,
                                ":hover": {
                                    color: theme?.custom.mainButtonHighlight,
                                }, 
                            }}
                            startIcon={
                                this.state.savingNew ? (
                                    <CircularProgress
                                        size={20}
                                        sx={{ color: "white" }}
                                    />
                                ) : (
                                    <AddCircleIcon color="primary" sx={{opacity:!this.props.isTeamMember ? 0.5 :1}}/>
                                )
                            }
                            disabled={!this.props.isTeamMember}
                            onClick={this.handleTemplateCreationFormOpen}
                        >
                            Create Template
                        </Button>
                    )}
                </Box>
                {this.renderTemplateList()}
            </Stack>
        );
    }
}

export const TemplateEditor = WithTemplates(TemplateEditorClass)